import {CompanyEntity} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, Switch, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {useVehicleForm} from "./useVehicleForm";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";

export interface VehicleFormProps {
  options?: VehicleFormOptions;
  defaultValues?: VehicleFormDefaultValues;
  onSubmit?: (values: VehicleFormValues) => void;
  submitting?: boolean;
}

export interface VehicleFormOptions {

}

export interface VehicleFormValues {
  name: string;
  barcode?: string;
  vin?: string;
  company: CompanyEntity | string;
  doNotUse?: boolean
}

export interface VehicleFormDefaultValues extends Partial<VehicleFormValues> {

}

export interface VehicleFormRawValues {
  name?: string;
  barcode?: string;
  vin?: string;
  company?: string;
  doNotUse?: boolean
}

export type VehicleFormType = UseFormReturnType<VehicleFormRawValues, (values: VehicleFormRawValues) => VehicleFormRawValues>

export const VehicleForm = (props: VehicleFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const region = useApp();

  const {t} = useTranslation(["common"]);

  const form = useVehicleForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        name: values.name!,
        barcode: values.barcode,
        vin: values.vin,
        company: values.company!,
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.vehicle.form.field.name", "Name")}
            placeholder={t<string>("common.entity.vehicle.form.field.name.placeholder", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.vehicle.form.field.vin", "VIN")}
            placeholder={t<string>("common.entity.vehicle.form.field.vin.placeholder", "VIN")}
            {...form.getInputProps("vin")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.vehicle.form.field.vin", "QR Code")}
            placeholder={t<string>("common.entity.vehicle.form.field.vin.placeholder", "QR Code")}
            {...form.getInputProps("barcode")} />
        </Grid.Col>
        <Divider my="sm" variant="dotted"/>
        <Grid.Col span={12}>
          <CompanySelect
            label={t("common.entity.vehicle.form.field.company", "Company")}
            placeholder={t<string>("common.entity.vehicle.form.field.company.placeholder", "Company")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("company")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.vehicle.form.field.donotuse", "Do Not Use")}
        placeholder={t<string>("common.entity.vehicle.form.field.donotuse", "Do Not Use")}
        {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface VehicleFormModalProps extends VehicleFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const VehicleFormModal = (props: VehicleFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <VehicleForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
